import * as React from "react";
import { ITaxonomyPickerProps } from "../../interfaces/props/ITaxonomyPickerProps";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { IPickerTerm, IPickerTerms, TaxonomyPicker } from "./taxonomyPicker";
import SPTermStorePickerService from "../../services/SPTermStorePickerService";
import { logger } from "../../../../utilities/logger";
import { isEqual } from "@microsoft/sp-lodash-subset";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";

const getTaxonomyFieldTerms = (terms: IPickerTerm[]) => {
  return {
    terms: terms?.map(term => {
      return {
        label: term.name,
        termGuid: term.key
      };
    })
  };
}

const Taxonomy: React.FunctionComponent<ITaxonomyPickerProps> = ({
  displayName,
  internalName,
  isRequired,
  sharepointService,
  itemLimit,
  inputValue,
  updateInputValidationStatus,
  inputValidator,
  sharepointListId,
  regionalSetting,
  webUrl
}: ITaxonomyPickerProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const [termSetId, setTermSetId] = React.useState<string>();
  const [defaultTerms, setDefaultTerms] = React.useState<IPickerTerm[]>([]);

  const prevTermsRef = React.useRef<IPickerTerm[]>([]);

  const onTaxPickerChange = (terms: IPickerTerms) => {
    prevTermsRef.current = terms;
    updateInputValidationStatus(
      inputValidator.validate(
        getTaxonomyFieldTerms(terms)
      )
    )
  };

  const taxonomyProps = {
    allowMultipleSelections: itemLimit > 1,
    termsetNameOrID: undefined,
    panelTitle: languageStrings.SelectTerm,
    label: displayName,
    required: isRequired(),
    context: { webUrl: webUrl, regionalLocalId: Number(regionalSetting?.LocaleId) ? regionalSetting.LocaleId : 1033 },
    onChange: onTaxPickerChange,
    isTermSetSelectable: false,
    useSessionStorage: true,
    initialValues: undefined,
    validateInput: true
  };

  React.useEffect(() => {
    (async () => {
      const termSetResult: any = await sharepointService.getTaxonomyTermSet(sharepointListId, internalName);
      if (termSetResult?.TermSetId) {
        setTermSetId(termSetResult.TermSetId);
      }
    })();
  }, []);

  React.useEffect(() => {
    if (termSetId) {
      (async () => {
        try {
          let terms: IPickerTerm[];
          const termsToFind: string[] = Array.isArray(inputValue?.value)
            ? inputValue.value
            : Array.isArray(inputValue?.value?.terms)
              ? inputValue.value.terms.map(term => term.label)
              : null;

          if (termsToFind?.length > 0) {
            const termsService = new SPTermStorePickerService({ ...taxonomyProps, termsetNameOrID: termSetId });
            terms = await termsService.findTerms(termsToFind, true);
          }

          setDefaultTerms(terms?.length > 0 ? terms : []);
        } catch (error) {
          logger(error, "taxonomy.tsx (useEffect)");
        }
      })();
    }
  }, [inputValue?.value, termSetId]);

  React.useEffect(() => {
    if (!isEqual(defaultTerms, prevTermsRef.current)) {
      prevTermsRef.current = defaultTerms;
      updateInputValidationStatus(
        inputValidator.validate(getTaxonomyFieldTerms(defaultTerms))
      );
    }
  }, [defaultTerms]);

  const getTaxonomyProps = () => {
    return { ...taxonomyProps, termsetNameOrID: termSetId, initialValues: defaultTerms, readOnly: inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true ? true : false };
  };

  return (termSetId && <TaxonomyPicker {...getTaxonomyProps()} />) ?? null;
};

export default Taxonomy;